import React, { useEffect, useState, useMemo } from "react";
import { Flex, Box, Text, Divider, Badge } from "@chakra-ui/react";
import axios from "axios";
import { MessageSquareWarning } from "lucide-react";
import InfoCard from "components/Card/InfoCard";
import PackageReports from "./PackageReports";
import DriverTrackingMap from "./DriverTrackingMap";
import getStatusColor from "../utils/StatusColor";

const ManagePackage = ({ package: pkgData, onClose }) => {
  const [routeCoords, setRouteCoords] = useState([]);
  const [eta, setEta] = useState(null);
  const [totalDuration, setTotalDuration] = useState(null);

  const start = pkgData?.original?.package?.pickup_lat && pkgData?.original?.package?.pickup_lon
    ? { latitude: pkgData.original.package.pickup_lat, longitude: pkgData.original.package.pickup_lon }
    : null;

  const destination = pkgData?.original?.package?.dropoff_lat && pkgData?.original?.package?.dropoff_lon
    ? { latitude: pkgData.original.package.dropoff_lat, longitude: pkgData.original.package.dropoff_lon }
    : null;

  const driverLocation = pkgData?.original?.delivery?.driver?.driver_lat && pkgData?.original?.delivery?.driver?.driver_lon
    ? { latitude: pkgData.original.delivery.driver.driver_lat, longitude: pkgData.original.delivery.driver.driver_lon }
    : null;

  const packageStatus = pkgData?.original?.package?.package_status || "Unknown";

  // Fetch route once coordinates are available
  useEffect(() => {
    if (!start || !destination) return;

    const fetchRoute = async () => {
      try {
        const response = await axios.get(
          `https://api.mapbox.com/directions/v5/mapbox/driving/${start.longitude},${start.latitude};${destination.longitude},${destination.latitude}`,
          { params: { access_token: process.env.REACT_APP_MAPBOX_TOKEN, geometries: "geojson" } }
        );

        const route = response.data.routes[0];
        if (route?.geometry?.coordinates?.length) {
          setRouteCoords(route.geometry.coordinates.map(([lng, lat]) => ({ latitude: lat, longitude: lng })));
        }
      } catch (err) {
        console.error("Mapbox route error:", err);
      }
    };

    fetchRoute();
  }, [start?.latitude, start?.longitude, destination?.latitude, destination?.longitude]);

  // Fetch ETA once driverLocation and destination are available
  useEffect(() => {
    if (!driverLocation || !destination) return;

    const fetchETA = async () => {
      try {
        const response = await axios.get(
          `https://api.mapbox.com/directions/v5/mapbox/driving/${driverLocation.longitude},${driverLocation.latitude};${destination.longitude},${destination.latitude}`,
          { params: { access_token: process.env.REACT_APP_MAPBOX_TOKEN, geometries: "geojson", overview: "simplified" } }
        );

        const durationValue = response.data.routes[0]?.duration ?? null;
        setEta({ durationText: durationValue, durationValue });
        setTotalDuration((prev) => prev ?? durationValue);
      } catch (err) {
        console.error("ETA fetch error:", err);
      }
    };

    fetchETA();
  }, [driverLocation?.latitude, driverLocation?.longitude, destination?.latitude, destination?.longitude]);

  const formatDuration = (seconds) => {
    if (!seconds) return "Unknown";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    const hrText = hrs === 0 ? "" : hrs === 1 ? "1hr:" : `${hrs}hrs:`;
    return `${hrText}${mins.toString().padStart(2, "0")}min`;
  };

  const currentTrackingState = useMemo(() => {
    if (!pkgData?.original?.package) return {};
    const status = pkgData.original.package.package_status;
    const durationSeconds = eta?.durationValue ?? 0;
    let progress = 0;
    if (totalDuration && durationSeconds != null && ["Picked Up", "In Transit"].includes(status)) {
      const remaining = totalDuration - durationSeconds;
      progress = Math.floor((remaining / totalDuration) * 100);
      progress = Math.max(0, Math.min(100, progress));
    }
    const states = {
      "Awaiting Pick Up": { timeLeft: "0h:00min", progress: 0, bgColor: "#A7CCFE", textColor: "#071D54", state: "Awaiting Pick Up", stateNumber: 1 },
      "Picked Up": { timeLeft: formatDuration(durationSeconds), progress, bgColor: "#3BB54A", textColor: "#FFFFFF", state: "Picked Up", stateNumber: 2 },
      "In Transit": { timeLeft: formatDuration(durationSeconds), progress, bgColor: "#FAA634", textColor: "#FFFFFF", state: "In Transit", stateNumber: 3 },
      "Delivered": { timeLeft: "Delivered", progress: 100, bgColor: "#071D54", textColor: "#FFFFFF", state: "Delivered", stateNumber: 4 },
    };
    return states[status] || {};
  }, [pkgData, eta, totalDuration]);

  const completedCount = Math.floor(((currentTrackingState.progress || 0) / 100) * routeCoords.length);
  const completedRoute = routeCoords.slice(0, completedCount);
  const remainingRoute = routeCoords.slice(completedCount);

  return (
    <Flex direction="column" p={4}>
      <Flex mb={4}>
        <Text fontWeight="500" color="gray.500" fontSize="md">Package Status</Text>
        <Badge ml={3} colorScheme={getStatusColor(packageStatus)}>{packageStatus}</Badge>
      </Flex>

      <Divider my={6} />

      <Box mb={6}>
        <InfoCard heading="Track Driver" icon={<MessageSquareWarning />} headingFontSize="md" headingFontWeight="500" headingColor="gray.500">
          {start && destination ? (
            <DriverTrackingMap
              driverLocation={driverLocation}
              start={start}
              destination={destination}
              completedRoute={completedRoute}
              remainingRoute={remainingRoute}
              packageStatus={currentTrackingState.state || "Unknown"}
              height={300}
              startColor="#071D54"
              destinationColor="#1454F3"
            />
          ) : (
            <Text color="gray.400">Start or destination unavailable 🚫</Text>
          )}
        </InfoCard>
      </Box>

      <Box mb={6}>
        <InfoCard heading="Package Reports" icon={<MessageSquareWarning />} headingFontSize="md" headingFontWeight="500" headingColor="gray.500">
          <PackageReports reports={pkgData.mappedReports || []} onUpdateStatus={() => {}} />
        </InfoCard>
      </Box>
    </Flex>
  );
};

export default ManagePackage;
